import React from 'react';
import { Svg, Circle, Path, Text as SvgText } from '@react-pdf/renderer';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

import { toHex } from '../../../util/Colors';

/**
 * PDF-compatible Organ System Circle visualization.
 *
 * Matches the web OrganSystemCircle behavior:
 * - Inner ring: score as percentage, colored by score tier (green/yellow/red)
 * - Outer ring: confidence percentage, gray color
 * - Both start at 12:00 and fill clockwise
 * - Max score is 95 (not 100), so a full ring = 95%
 *
 * Uses SVG Path arcs since react-pdf doesn't support strokeDasharray/offset.
 */

// Convert polar coordinates to cartesian, with 0 degrees = 12 o'clock
function polarToCartesian(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return {
        x: cx + r * Math.cos(rad),
        y: cy + r * Math.sin(rad)
    };
}

// Generate SVG arc path from startAngle to endAngle (clockwise, degrees)
function arcPath(cx, cy, r, startAngle, endAngle) {
    if (endAngle - startAngle >= 360) {
        // Full circle needs two semicircles
        const mid = polarToCartesian(cx, cy, r, 180);
        const end = polarToCartesian(cx, cy, r, 359.99);
        const start = polarToCartesian(cx, cy, r, 0);
        return `M ${start.x} ${start.y} A ${r} ${r} 0 1 1 ${mid.x} ${mid.y} A ${r} ${r} 0 1 1 ${end.x} ${end.y}`;
    }
    const start = polarToCartesian(cx, cy, r, startAngle);
    const end = polarToCartesian(cx, cy, r, endAngle);
    const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        width: 100,
        marginBottom: 4
    },
    nameText: {
        fontSize: 12,
        fontWeight: 500,
        fontFamily: 'Helvetica',
        color: '#374151',
        textAlign: 'center',
        marginBottom: 2
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 2
    },
    detailLabel: {
        fontSize: 6,
        color: '#6b7280',
        fontFamily: 'Helvetica',
        marginRight: 2
    },
    detailValue: {
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        color: '#4b5563'
    }
});

function PDFOrganCircle({ displayName, score, confidence, color, size = 90 }) {
    const center = size / 2;
    const outerRadius = 40;
    const innerRadius = 32;
    const outerStrokeWidth = 4;
    const innerStrokeWidth = 10;

    const confidencePercent = confidence?.percentage || 0;
    const scorePercent = score != null ? score : 0;
    const isLowConfidence = confidence?.isLowConfidence;

    const displayColor = isLowConfidence ? '#9ca3af' : (toHex(color) || '#22c55e');
    const confidenceColor = '#6b7280';
    const bgRingColor = '#e5e7eb';

    // Calculate sweep angles (percentage of 360)
    const scoreAngle = Math.min((scorePercent / 100) * 360, 359.99);
    const confidenceAngle = Math.min((confidencePercent / 100) * 360, 359.99);

    // SVG total height includes space for name and confidence text
    const svgSize = size;

    return (
        <View style={styles.container}>
            {/* Organ name */}
            <Text style={styles.nameText}>{displayName}</Text>

            {/* SVG rings */}
            <Svg width={svgSize} height={svgSize}>
                {/* Background rings */}
                <Circle
                    cx={center}
                    cy={center}
                    r={outerRadius}
                    stroke={bgRingColor}
                    strokeWidth={outerStrokeWidth}
                    fill="none"
                />
                <Circle
                    cx={center}
                    cy={center}
                    r={innerRadius}
                    stroke={bgRingColor}
                    strokeWidth={innerStrokeWidth}
                    fill="none"
                />

                {/* Confidence arc (outer ring) */}
                {confidenceAngle > 0 && (
                    <Path
                        d={arcPath(center, center, outerRadius, 0, confidenceAngle)}
                        stroke={confidenceColor}
                        strokeWidth={outerStrokeWidth}
                        strokeLinecap="round"
                        fill="none"
                    />
                )}

                {/* Score arc (inner ring) */}
                {scoreAngle > 0 && !isLowConfidence && (
                    <Path
                        d={arcPath(center, center, innerRadius, 0, scoreAngle)}
                        stroke={displayColor}
                        strokeWidth={innerStrokeWidth}
                        strokeLinecap="round"
                        fill="none"
                    />
                )}

                {/* Score text in center */}
                <SvgText
                    x={center}
                    y={center + 6}
                    textAnchor="middle"
                    fill={displayColor}
                    fontSize={18}
                    fontFamily="Helvetica-Bold"
                >
                    {isLowConfidence ? 'N/A' : score}
                </SvgText>
            </Svg>

            {/* Confidence label */}
            <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Conf:</Text>
                <Text style={[styles.detailValue, {
                    color: isLowConfidence ? '#9ca3af' : '#4b5563'
                }]}>
                    {confidencePercent}%
                </Text>
            </View>
        </View>
    );
}

export default PDFOrganCircle;
